import React, { useContext, useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useInformativos } from '../../context/InformativosContext';
import { useNavigate } from 'react-router-dom';
import './NavBar.css';
import Drawer from '../drawer/Drawer';
import api from '../../services/api';

export default function NavBar() {
  const { user, company, logout } = useContext(AuthContext);
  const { unviewedCount } = useInformativos();
  const location = useLocation();
  const navigate = useNavigate();

  const [lastScrollY, setLastScrollY] = useState(0);
  const [visible, setVisible] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const controlNavbar = useCallback(() => {
    if (typeof window !== 'undefined') {
      // Oculta a navbar se rolar para baixo e estiver a mais de 80px do topo.
      if (window.scrollY > lastScrollY && window.scrollY > 80) {
        setVisible(false);
      } else { // Mostra a navbar se rolar para cima.
        setVisible(true);
      }
      // Atualiza a última posição de rolagem.
      setLastScrollY(window.scrollY);
    }
  }, [lastScrollY]);

  useEffect(() => {
    // Close menu on route change
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    window.addEventListener('scroll', controlNavbar);
    return () => {
      window.removeEventListener('scroll', controlNavbar);
    };
  }, [controlNavbar]);

  const handleLogout = () => {
    logout();
    navigate('/login')
  };

  const getRoleLabel = (role) => {
    const roles = {
      SUPER_ADMIN: 'Super-Admin',
      ADMIN_EAD: 'Admin',
      ADMIN_CLIENT: 'Admin',
      INSTRUCTOR: 'Instrutor',
      STUDENT: 'Aluno',
      GUEST: 'Convidado',
    };
    return roles[role] || 'Usuário';
  };
  
  // Don't show navbar on login/register pages
  if (location.pathname === '/login' || location.pathname === '/register') {
    return null;
  }

  // Resolve corretamente a URL da logo
  // Se company.logoUrl for apenas um nome de arquivo (UUID), monta a URL usando a variável de ambiente
  let logoSrc = '/logo.png';
  if (company?.logoUrl) {
    const saved = company.logoUrl;

    // Verifica se é apenas o nome do arquivo (UUID) ou já é uma URL completa
    if (saved.startsWith('http://') || saved.startsWith('https://')) {
      // Já é uma URL completa - usa direto (fallback para URLs antigas)
      logoSrc = saved;
    } else if (saved.includes('/')) {
      // Contém barra - é um caminho relativo antigo, trata como upload
      const apiBase = process.env.REACT_APP_URL_BASE || '';
      const apiOrigin = apiBase.replace(/\/api\/?$/i, '').replace(/\/$/, '');
      logoSrc = `${apiOrigin}${saved}`;
    } else {
      // É apenas o nome do arquivo - constrói a URL usando REACT_APP_URL_BASE
      const apiBase = process.env.REACT_APP_URL_BASE || '';
      const apiOrigin = apiBase.replace(/\/api\/?$/i, '').replace(/\/$/, '');
      logoSrc = `${apiOrigin}/uploads/${saved}`;
    }
  }

  return (
    <nav className={`navbar ${visible ? 'navbar--visible' : 'navbar--hidden'}`}>
      <div className="navbar-container">
        <div className="navbar-brand">
          <NavLink to="/" className="brand-link">
            <div className="brand-icon">
              <img
                src={logoSrc}
                alt={company?.name || 'Logo da Empresa'}
                style={{ width: '60px', height: '60px', objectFit: 'contain' }}
              />
            </div>
            <span className="brand-text">{company?.name || 'LMS Platform'}</span>
          </NavLink>
        </div>

        <div className="navbar-menu">
          <div className="nav-links">
            {user?.role === 'SUPER_ADMIN' ? (
              <NavLink
                to="/control-panel"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
                <span>Painel de Controle</span>
              </NavLink>
            ) : (
              <>
                <NavLink
                  to="/"
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    <polyline points="9,22 9,12 15,12 15,22" />
                  </svg>
                  <span>Dashboard</span>
                </NavLink>

                {user && ['ADMIN_EAD', 'ADMIN_CLIENT'].includes(user.role) && (
                  <NavLink
                    to="/users"
                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 00-3-3.87" />
                      <path d="M16 3.13a4 4 0 010 7.75" />
                    </svg>
                    <span>Usuários</span>
                  </NavLink>
                )}

                {user && ['INSTRUCTOR', 'ADMIN_EAD'].includes(user.role) && (
                  <NavLink
                    to="/courses"
                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="16" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                    <span>Cursos</span>
                  </NavLink>
                )}

                <NavLink
                  to="/informativos"
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16v12H4V4z" />
                    <line x1="4" y1="16" x2="20" y2="16" />
                  </svg>
                  <span>Informativos</span>
                  {unviewedCount > 0 && (
                    <span className="notification-badge">
                      {unviewedCount > 99 ? '99+' : unviewedCount}
                    </span>
                  )}
                </NavLink>

                {user && ['ADMIN_EAD', 'ADMIN_CLIENT', 'INSTRUCTOR'].includes(user.role) && (
                  <NavLink
                    to="/reports"
                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21.21 15.89A10 10 0 1 1 8.11 2.79"></path>
                      <path d="M22 12A10 10 0 0 0 12 2v10z"></path>
                    </svg>
                    <span>Relatórios</span>
                  </NavLink>
                )}
              </>
            )}
          </div>

          <div className="navbar-user">
            <NavLink to={user ? '/profile' : '/'} className="user-info-link">
              <div className="user-info">
                <div className="user-avatar">
                  {(user && user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U')}
                </div>
                <div className="user-details">
                  <span className="user-name">{(user && user.fullName) || 'Usuário'}</span>
                  <span className="user-role">{getRoleLabel(user?.role)}</span>
                </div>
              </div>
            </NavLink>
            
            <button className="logout-btn" onClick={handleLogout} title="Sair">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                <polyline points="16,17 21,12 16,7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Sair</span>
            </button>
          </div>
        </div>

        <div className="mobile-menu-toggle">
          <button
            className="mobile-menu-btn"
            onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
            aria-expanded={menuOpen}
            aria-label="Abrir menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
            {unviewedCount > 0 && (
              <span className="notification-badge-hamburger">
                {unviewedCount > 99 ? '99+' : unviewedCount}
              </span>
            )}
          </button>
        </div>

        <Drawer open={menuOpen} onClose={() => setMenuOpen(false)}>
          <NavLink to={user ? '/profile' : '/'} className="user-info-link-mobile">
            <div className="drawer-header">
              <div className="drawer-user-info">
                <div className="user-avatar-large">
                  {(user && user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U')}
                </div>
                <div className="user-details-mobile">
                  <span className="user-name-mobile">{(user && user.fullName) || 'Usuário'}</span>
                  <span className="user-role-mobile">{getRoleLabel(user?.role)}</span>
                </div>
              </div>
            </div>
          </NavLink>

          <div className="drawer-nav-links">
            {user?.role === 'SUPER_ADMIN' ? (
              <NavLink
                to="/control-panel"
                className={({ isActive }) => `drawer-nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
                <span>Painel de Controle</span>
              </NavLink>
            ) : (
              <>
                <NavLink
                  to="/"
                  className={({ isActive }) => `drawer-nav-link ${isActive ? 'active' : ''}`}
                  onClick={() => setMenuOpen(false)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    <polyline points="9,22 9,12 15,12 15,22" />
                  </svg>
                  <span>Dashboard</span>
                </NavLink>

                <NavLink
                  to="/informativos"
                  className={({ isActive }) => `drawer-nav-link ${isActive ? 'active' : ''}`}
                  onClick={() => setMenuOpen(false)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16v12H4V4z" />
                    <line x1="4" y1="16" x2="20" y2="16" />
                  </svg>
                  <span>Informativos</span>
                  {unviewedCount > 0 && (
                    <span className="notification-badge-mobile">
                      {unviewedCount > 99 ? '99+' : unviewedCount}
                    </span>
                  )}
                </NavLink>

                {user && ['ADMIN_EAD', 'ADMIN_CLIENT', 'INSTRUCTOR'].includes(user.role) && (
                  <NavLink
                    to="/reports"
                    className={({ isActive }) => `drawer-nav-link ${isActive ? 'active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21.21 15.89A10 10 0 1 1 8.11 2.79"></path>
                      <path d="M22 12A10 10 0 0 0 12 2v10z"></path>
                    </svg>
                    <span>Relatórios</span>
                  </NavLink>
                )}
              </>
            )}
          </div>

          <div className="drawer-footer">
            <button className="drawer-logout-btn" onClick={handleLogout} title="Sair">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                <polyline points="16,17 21,12 16,7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Sair</span>
            </button>
          </div>
        </Drawer>
      </div>
    </nav>
  );
}